import { ThreadMessageTypeType } from "../ThreadMessageQueueItem";
import { Type } from "@sinclair/typebox";
import { IMSelectorsUnion } from "../integrations/im/IMSelectorsTypes";
import { ParsedBlocklistRecord, ReportMetadata } from "../messages-server/messages";
import { WhatsllyGroupType } from "@whatslly/types/WhatsllyGroup";
import type { Logger } from "../logger";

export const AllChats = Symbol("AllChats");
export const InjectMessageEmitter = Symbol("InjectMessageEmitter");

export const ReportMessagesRequest = Type.Union([
  ReportMetadata,
  Type.Object({
    messages: Type.Array(Type.Object({ _id: Type.String() }, { additionalProperties: true })),
  }),
]);

type MessageQueueItemBase = ReportedMessage & {
  query?: CompactQuery | CompactQuery[];
  contactPhone: string | undefined;
};

export type MessageQueueItem =
  | (MessageQueueItemBase & { channelType: "telegram"; deferredMedia?: TelegramDeferredMedia })
  | (MessageQueueItemBase & { channelType: "whatsAppWeb"; deferredMedia?: WhatsAppDeferredMedia })
  | (MessageQueueItemBase & { channelType?: Exclude<MessageChannelType, "telegram" | "whatsAppWeb"> | undefined; deferredMedia?: never });

export interface CompactQuery {
  personId?: string;
  personType?: string;
  recordTypeId?: string;
  engagementId?: string;
  wpid?: string;
  groupId?: string;
  groupMembers?: WhatsllyGroupType["members"];
}

export interface SerializedMessageMedia {
  mimeType: string;
  fileName: string;
  size: number;
  dataURI?: string;
  binary?: string;
  invalid?: "size" | "mimeType" | "messageType" | undefined;
}

export const MESSAGE_CHANNEL_TYPES = [
  "whatsAppWeb",
  "telegram",
  "whatsAppApi",
  "imessage",
  "sms",
  "androidNativeRecording",
  "twilioVoip",
  "line",
  "signal",
  "teams-archive",
  "discord-archive",
] as const;
export type MessageChannelType = (typeof MESSAGE_CHANNEL_TYPES)[number];

export const getChannelTypeToText = (channelType: MessageChannelType): string => {
  switch (channelType) {
    case "androidNativeRecording":
      return "Call recording";
    case "imessage":
      return "iMessage";
    case "sms":
      return "SMS";
    case "telegram":
      return "Telegram";
    case "twilioVoip":
      return "Voice";
    case "whatsAppApi":
      return "WhatsApp Business API";
    case "whatsAppWeb":
      return "WhatsApp";
    case "line":
      return "LINE";
    case "signal":
      return "Signal";
    case "teams-archive":
      return "Teams Archive";
    case "discord-archive":
      return "Discord Archive";
  }
};

interface BaseMessage {
  chatType?: "newsletter" | "chat" | "status" | "broadcast" | "callRecording";
  chatId: string;
  userEmail?: string;
  id: string;
  serializedMessageId: string;
  shortId: string;
  text?: string;
  t: number;
  timestamp: number;
  isSentByMe: boolean;
  isGroupMsg: boolean;
  isSavedContact: boolean;
  isSpamContact: boolean;
  isEdited?: boolean;
  isDeleted?: boolean;
  isDisappearing?: boolean;
  isForwarded?: boolean;
  type: string;
  senderName: string;
  senderId: string;
  senderPhone: string;
  receiverName: string;
  receiverId: string;
  receiverPhone: string;
  notifyName: string;
  authorPhone: string;
  startOfDay: number;
  quotedMsgId?: string;
  ack?: "sent" | "delivered" | "read";
  latestUpdate: number;
  lastFraudCheck?: number;
  vcards?: { names: string; count: number };
  channelType?: MessageChannelType;
  groupParticipants?: string[];
  location?: { latitude: number; longitude: number };
  pollData?: { name: string; options: string[] };
  subtype?: string;
  affectedParticipants?: { phone: string; name?: string }[];
}
export type SerializedMessageBase = BaseMessage & {
  media?: SerializedMessageMedia;
  transcriptText?: string;
  templateId?: string;
};

export type SerializedMessage =
  | (SerializedMessageBase & { channelType: "telegram"; deferredMedia?: TelegramDeferredMedia })
  | (SerializedMessageBase & { channelType: "whatsAppWeb"; deferredMedia?: WhatsAppDeferredMedia })
  | (SerializedMessageBase & { channelType?: Exclude<MessageChannelType, "telegram" | "whatsAppWeb"> | undefined; deferredMedia?: never });

export type ReportedMessage = Omit<SerializedMessageBase, "affectedParticipants" | "subtype"> & {
  _id: string;
  startTime?: number;
  endTime?: number;
  packageName?: string;
  userName?: string;
  userGroups?: string;
  media?: SerializedMessageMedia & {
    fileUrl?: string;
    fileId?: string;
  };
  transcriptText?: string;
};

export const SerializedMessageMediaSchemaTypeBox = Type.Object({
  mimeType: Type.String(),
  fileName: Type.String(),
  size: Type.Number(),
  dataURI: Type.Optional(Type.String()),
  binary: Type.Optional(Type.String()),
  invalid: Type.Optional(Type.Union([Type.Literal("size"), Type.Literal("mimeType"), Type.Literal("messageType")])),
});

export const BaseMessageSchemaTypeBox = Type.Object({
  chatId: Type.String(),
  userEmail: Type.Optional(Type.String()),
  id: Type.String(),
  serializedMessageId: Type.String(),
  shortId: Type.String(),
  text: Type.Optional(Type.String()),
  t: Type.Number(),
  timestamp: Type.Number(),
  isSentByMe: Type.Boolean(),
  isGroupMsg: Type.Boolean(),
  chatType: Type.Optional(Type.Union([Type.Literal("newsletter"), Type.Literal("chat"), Type.Literal("status"), Type.Literal("broadcast")])),
  isSavedContact: Type.Boolean(),
  isSpamContact: Type.Boolean(),
  isEdited: Type.Optional(Type.Boolean()),
  isDeleted: Type.Optional(Type.Boolean()),
  isDisappearing: Type.Optional(Type.Boolean()),
  isForwarded: Type.Optional(Type.Boolean()),
  type: Type.String(),
  senderName: Type.String(),
  senderId: Type.String(),
  senderPhone: Type.String(),
  receiverName: Type.String(),
  receiverId: Type.String(),
  receiverPhone: Type.String(),
  notifyName: Type.String(),
  authorPhone: Type.String(),
  startOfDay: Type.Number(),
  quotedMsgId: Type.Optional(Type.String()),
  ack: Type.Optional(Type.Union([Type.Literal("sent"), Type.Literal("delivered"), Type.Literal("read")])),
  latestUpdate: Type.Number(),
  lastFraudCheck: Type.Optional(Type.Number()),
  vcards: Type.Optional(
    Type.Object({
      names: Type.String(),
      count: Type.Number(),
    })
  ),
  groupParticipants: Type.Optional(Type.Array(Type.String())),
  location: Type.Optional(
    Type.Object({
      latitude: Type.Number(),
      longitude: Type.Number(),
    })
  ),
});

export const SerializedMessageSchemaTypeBox = Type.Intersect([
  BaseMessageSchemaTypeBox,
  Type.Object({
    media: Type.Optional(SerializedMessageMediaSchemaTypeBox),
    templateId: Type.Optional(Type.String()),
  }),
]);

export type TelegramDeferredMedia = {
  id: string;
  accessHash: string;
  fileReference: string;
  dcId: number;
  mediaType: "document" | "photo" | "video" | "audio" | "voice" | "other";
  thumbSize?: string;
};

export type WhatsAppDeferredMedia = { url: string; key: string; encFilehash?: string };
export type DeferredMediaMeta = { messageId?: string; size?: number };
export type WhatsappMediaDiagnostics = DeferredMediaMeta & { encFilehash?: string };
export type DeferredMedia = WhatsAppDeferredMedia | TelegramDeferredMedia;

type EmittedMessageBase = BaseMessage & {
  media?: SerializedMessageMedia;
  mediaMetadata?: SerializedMessageMedia;
  loadMedia: (timeout?: number) => Promise<SerializedMessage>;
  loadSignedMedia?: (timeout?: number) => Promise<SerializedMessage>;
};

export type EmittedMessage =
  | (EmittedMessageBase & { channelType: "telegram"; deferredMedia?: TelegramDeferredMedia })
  | (EmittedMessageBase & { channelType: "whatsAppWeb"; deferredMedia?: WhatsAppDeferredMedia })
  | (EmittedMessageBase & { channelType?: Exclude<MessageChannelType, "telegram" | "whatsAppWeb"> | undefined; deferredMedia?: never });
export interface IMessageEmitterInstance {
  me: { id?: string; server?: string; phone?: string; isBusiness?: boolean; name?: string } | undefined;
  [Symbol.asyncIterator](): AsyncGenerator<EmittedMessage>;
}
export interface IMessageEmitter<C, M> {
  new (chatModel: M | typeof AllChats, settings?: Partial<IEmitterSettings>, logger?: Logger): IMessageEmitterInstance;
  inject(selectors: IMSelectorsUnion): void;
  getContactPhone(contact?: C): string;
  relativeDateStr(unixTime: number): string | undefined;
  getActiveChat(): M | undefined;
  getMe(): { id?: string; server?: string; phone?: string; isBusiness?: boolean; name?: string } | undefined;
}
export interface IEmitterSettings {
  syncNewsLetterMessages?: boolean;
  maxMessageAge: number;
  pollMessageTypes?: Set<ThreadMessageTypeType>;
  eventMessageTypes?: Set<ThreadMessageTypeType>;
  mediaMessageTypes: Set<ThreadMessageTypeType>;
  mediaMessageMimeTypes: Set<string> | undefined;
  vcardMessageTypes: Set<ThreadMessageTypeType>;
  textMessageTypes: Set<ThreadMessageTypeType>;
  mediaMode?: "binary" | "base64";
  maxFileSizeBytes: number;
  listenToNewMessages: boolean;
  scrollToEarliestMessage: boolean;
  abortSignal: AbortSignal;
  emitAckChanges: boolean;
  // TODO: remove string[] after update
  blockedPhones: ParsedBlocklistRecord[] | string[];
  phonesWhitelist?: Set<string>;
  syncStatusMessages?: boolean;
  messageIdsToSync?: Set<string>;
  syncTimestampRange?: { oldest: number; newest: number };
  scrollTimestamps?: Record<string, number>;
  ignoreOldMessages?: boolean;
}

export type ReportMessagesRequest = ReportMetadata & { messages: MessageQueueItem[] };
